import { useCallback, useEffect, useState } from 'react';
import { useGetOrderFirstPageAPI, useGetOrdersMappingAPI } from '../features/orders/Shared/hooks';
import { TAssignOrder, TOrderMapping } from '../types';
import { useFetchRemainingOrders } from '../features/orders/Orders/useFetchRemainingOrders';
import { TGetOrdersRes } from '../types/responses/orders.type';
import { useAppSelector } from './useAppStore';
import { getCameFromProductExchanges } from '../store/orders/selectors';

type TOptions = {
  prevTab?: string
};

export function useCommonOrders(options?: TOptions) {
  const [isFetchByDates, setIsFetchByDate] = useState(false);
  const cameFromProductExchanges = useAppSelector(getCameFromProductExchanges);

  const { data } = useGetOrdersMappingAPI() as {
    data: {
      ordersMapping: Array<TOrderMapping>
    }
  };

  // Fetch the first page of orders
  const {
    data: firstPageData, isRefetching,
  } = useGetOrderFirstPageAPI({
    isMainOrdersPage: !!data && options?.prevTab === '' && !cameFromProductExchanges,
  });

  const { refetch } = useFetchRemainingOrders(firstPageData as TGetOrdersRes, data?.ordersMapping);

  const fetchRemainingOrdersWithWorker = useCallback(async () => {
    if (firstPageData || (firstPageData && isFetchByDates)) {
      try {
        if ('hasNext' in firstPageData && options?.prevTab === '' && !cameFromProductExchanges) {
          const fetchedOrders = await refetch() as { data: Array<TAssignOrder> };

          if (fetchedOrders?.data?.length > 0) {
            setIsFetchByDate(false);
          }
        }
      } catch (error) {
        console.error('Error fetching remaining orders:', error);
      }
    }
  }, [firstPageData]);

  useEffect(() => {
    if (firstPageData && 'orders' in firstPageData && firstPageData?.orders?.length && firstPageData?.hasNext && !isRefetching) {
      fetchRemainingOrdersWithWorker();
    }
  }, [firstPageData, fetchRemainingOrdersWithWorker, isRefetching]);

  return {
    firstPageData,
    isRefetching,
    ordersMapping: data?.ordersMapping,
    fetchRemainingOrdersWithWorker,
    setIsFetchByDate,
    isFetchByDates,
  };
}
